import React, { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { Cpu, MemoryStick, Network, Bot } from 'lucide-react';
import { GlassCard } from './SharedUI';
import { useSystemStats } from '@/hooks/useSystemStats';

function fmtRate(bytesPerSec: number): string {
  const kbps = bytesPerSec / 1024;
  if (kbps >= 1024) return `${(kbps / 1024).toFixed(1)} MB/s`;
  if (kbps >= 1) return `${kbps.toFixed(1)} KB/s`;
  return `${Math.max(0, bytesPerSec).toFixed(0)} B/s`;
}

function Bar({ value, gradient }: { value: number; gradient: string }) {
  return (
    <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
      <div
        className="h-full rounded-full transition-all duration-500"
        style={{ width: `${Math.min(100, Math.max(0, value))}%`, background: gradient }}
      />
    </div>
  );
}

/**
 * 中枢视图实时资源面板（A 优化）
 * 接 useSystemStats() 真实 CPU/内存/网络（Socket 实时 + HTTP 兜底，三端同源）
 * 接 /api/agents 真实智能体数量 —— 替换原 ContributorNodePanel 的写死假数字
 */
export function NexusStatsPanel({ t, orgConnection }: { t?: any; orgConnection?: any }) {
  const isZh = t?.langCode !== 'en';
  const { stats, netRate } = useSystemStats();
  const [agentCount, setAgentCount] = useState<number | null>(null);

  useEffect(() => {
    let active = true;
    fetch('/api/agents', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => {
        if (!active) return;
        setAgentCount(Array.isArray(d) ? d.length : (d.agents?.length ?? 0));
      })
      .catch(() => { if (active) setAgentCount(null); });
    return () => { active = false; };
  }, []);

  const cpu = stats?.cpu_percent ?? 0;
  const memPct = stats?.memory_percent ?? 0;
  const memUsed = stats?.memory_used_gb ?? 0;
  const memTotal = stats?.memory_total_gb ?? 0;

  return (
    <motion.div
      initial={{ opacity: 0, x: -40 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -40 }}
      transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
      className="w-72"
    >
      <GlassCard className="p-4 rounded-[1.5rem] space-y-3 border-white/5 bg-black/30 backdrop-blur-3xl">
        <div className="flex items-center gap-2">
          <Network size={14} className="text-celestial-saturn" />
          <span className="text-xs font-black text-white/70">
            {t?.nexusLiveStats || (isZh ? '中枢实时资源' : 'Nexus Live Stats')}
          </span>
        </div>

        {/* Org connection */}
        <div className="flex items-center justify-between px-1 pt-1">
          <div className="text-center">
            <div className="text-sm font-black text-celestial-saturn truncate max-w-[10rem]">
              {orgConnection?.orgName || (isZh ? '未加入组织' : 'No Org')}
            </div>
            <div className="text-xs text-white/45 font-bold uppercase">
              {orgConnection?.connected
                ? (isZh ? '组织·在线' : 'ORG·ONLINE')
                : (isZh ? '组织·离线' : 'ORG·OFFLINE')}
            </div>
          </div>
          <div className="w-px h-6 bg-white/5" />
          <div className="text-center">
            <div className="text-sm font-black text-white/50">{orgConnection?.connected ? (isZh ? '已连接' : 'LINKED') : '—'}</div>
            <div className="text-xs text-white/45 font-bold uppercase">{t?.nodes || (isZh ? '节点' : 'Nodes')}</div>
          </div>
        </div>

        {/* CPU */}
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1 text-white/45"><Cpu size={12} />{t?.cpu || (isZh ? 'CPU' : 'CPU')}</span>
            <span className="font-black text-white/70">{cpu.toFixed(1)}%</span>
          </div>
          <Bar value={cpu} gradient="linear-gradient(90deg,#10b981,#a7f3d0)" />
        </div>

        {/* Memory */}
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1 text-white/45"><MemoryStick size={12} />{t?.memory || (isZh ? '内存' : 'Memory')}</span>
            <span className="font-black text-white/70">{memUsed.toFixed(1)}/{memTotal.toFixed(0)} GB</span>
          </div>
          <Bar value={memPct} gradient="linear-gradient(90deg,#a855f7,#e2b0ff)" />
        </div>

        {/* Network */}
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1 text-white/45"><Network size={12} />{t?.network || (isZh ? '网络' : 'Network')}</span>
            <span className="font-black text-white/70">↓{fmtRate(netRate.rx)} ↑{fmtRate(netRate.tx)}</span>
          </div>
        </div>

        {/* Agents + status */}
        <div className="flex items-center justify-between px-1 pt-1">
          <div className="text-center">
            <div className="text-sm font-black text-celestial-saturn">{agentCount === null ? '—' : agentCount}</div>
            <div className="text-xs text-white/45 font-bold uppercase flex items-center justify-center gap-1"><Bot size={10} />{t?.agents || (isZh ? '智能体' : 'Agents')}</div>
          </div>
          <div className="w-px h-6 bg-white/5" />
          <div className="text-center">
            <div className="text-sm font-black text-white/50">{stats ? (isZh ? '在线' : 'ONLINE') : '—'}</div>
            <div className="text-xs text-white/45 font-bold uppercase">{t?.status || (isZh ? '状态' : 'Status')}</div>
          </div>
        </div>
      </GlassCard>
    </motion.div>
  );
}
